<template>
  <div
    class="form-group row form-group-border-bottom align-items-center file-upload-field"
    :class="{ 'file-upload-dragover': dragDepth > 0 }"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <div
      ref="trigger"
      class="col d-flex align-items-center pointer file-upload-trigger"
      role="button"
      tabindex="0"
      :aria-labelledby="`${labelId} ${textId}`"
      :aria-describedby="describedBy"
      :aria-invalid="showError ? 'true' : undefined"
      :aria-required="field.required ? 'true' : undefined"
      @click="openPicker"
      @keydown.enter.prevent="openPicker"
      @keydown.space.prevent="openPicker"
    >
      <span :id="labelId" class="col-form-label file-upload-label">
        <span v-if="field.required" class="required">*</span>
        <span v-html="label"></span>
      </span>
      <span :id="textId" class="flex-grow-1 file-upload-text" :class="{ 'file-upload-placeholder': !fileName }">{{ fileName || field.default_text || "" }}</span>
      <img :src="uploadIcon" class="file-upload-icon" alt="" aria-hidden="true" />
    </div>
    <button
      v-if="fileName"
      type="button"
      class="col-auto file-upload-remove"
      aria-label="הסרת קובץ"
      @click="clearFile"
    >×</button>
    <input
      ref="input"
      type="file"
      class="file-upload-input"
      tabindex="-1"
      aria-hidden="true"
      :name="name"
      :accept="field.accept || undefined"
      :required="field.required"
      data-validate-on-submit="true"
      @change="onPickerChange"
    >
    <div
      class="col-12 invalid-feedback client-validate"
      :id="`${name}invalid`"
      :class="{ show: showError }"
    >{{ shownError }}</div>
  </div>
</template>
<script>
const MB = 1024 * 1024;

function hasFiles(event) {
  const types = event?.dataTransfer?.types;
  return !!types && Array.prototype.indexOf.call(types, "Files") >= 0;
}

export default {
  name: "form-file-upload-field",
  props: {
    label: String,
    name: String,
    field: Object,
  },
  emits: ["change"],
  data() {
    return {
      fileName: "",
      validFile: null,
      dragDepth: 0,
      showError: false,
      shownError: "",
      ownerForm: null,
      boundFormValidate: null,
      boundWindowDrag: null,
      uid: `file-upload-${this._.uid}`,
    };
  },
  computed: {
    uploadIcon() {
      return require("@/assets/icons/upload.svg");
    },
    labelId() {
      return `${this.uid}-label`;
    },
    textId() {
      return `${this.uid}-text`;
    },
    describedBy() {
      return this.showError ? `${this.name}invalid` : undefined;
    },
    acceptTokens() {
      return String(this.field?.accept || "")
        .split(",")
        .map((token) => token.trim().toLowerCase())
        .filter(Boolean);
    },
  },
  mounted() {
    this.boundFormValidate = (event) => this.onFormValidate(event);
    this.ownerForm = this.$el?.closest?.("form") || null;
    this.ownerForm?.addEventListener("yummi-validate", this.boundFormValidate);

    this.boundWindowDrag = (event) => {
      if (hasFiles(event)) {
        event.preventDefault();
      }
    };
    window.addEventListener("dragover", this.boundWindowDrag);
    window.addEventListener("drop", this.boundWindowDrag);
  },
  beforeUnmount() {
    this.ownerForm?.removeEventListener("yummi-validate", this.boundFormValidate);
    window.removeEventListener("dragover", this.boundWindowDrag);
    window.removeEventListener("drop", this.boundWindowDrag);
  },
  methods: {
    openPicker() {
      this.$refs.input?.click();
    },
    onPickerChange() {
      const file = this.$refs.input?.files?.[0];
      if (!file) {
        // Some browsers clear the input when the picker is cancelled.
        this.setInputFile(this.validFile);
        return;
      }
      this.selectFile(file);
    },
    onDragEnter(event) {
      if (hasFiles(event)) {
        this.dragDepth += 1;
      }
    },
    onDragOver(event) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "copy";
      }
    },
    onDragLeave() {
      if (this.dragDepth > 0) {
        this.dragDepth -= 1;
      }
    },
    onDrop(event) {
      this.dragDepth = 0;
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        this.selectFile(file);
      }
    },
    selectFile(file) {
      const error = this.fileError(file);
      if (error) {
        this.setInputFile(this.validFile);
        this.setError(error);
        return;
      }
      this.validFile = file;
      this.fileName = file.name;
      this.setInputFile(file);
      this.setError("");
      this.$emit("change");
    },
    clearFile() {
      this.validFile = null;
      this.fileName = "";
      this.setInputFile(null);
      this.$emit("change");
      this.$nextTick(() => this.$refs.trigger?.focus());
    },
    setInputFile(file) {
      const input = this.$refs.input;
      if (!input) {
        return;
      }
      if (input.files?.length === (file ? 1 : 0) && (!file || input.files[0] === file)) {
        return;
      }
      try {
        const transfer = new DataTransfer();
        if (file) {
          transfer.items.add(file);
        }
        input.files = transfer.files;
      } catch {
        if (!file) {
          input.value = "";
        }
      }
    },
    fileError(file) {
      if (!this.matchesAccept(file)) {
        return this.field?.type_error_message || "";
      }
      const maxMb = Number(this.field?.max_size_mb);
      if (Number.isFinite(maxMb) && maxMb > 0 && file.size > maxMb * MB) {
        return this.field?.size_error_message || "";
      }
      return null;
    },
    matchesAccept(file) {
      if (this.acceptTokens.length === 0) {
        return true;
      }
      const mime = String(file.type || "").toLowerCase();
      const fileName = String(file.name || "").toLowerCase();
      const dot = fileName.lastIndexOf(".");
      const ext = dot >= 0 ? fileName.substring(dot) : "";
      return this.acceptTokens.some((token) => {
        if (token.startsWith(".")) {
          return ext === token;
        }
        if (token.endsWith("/*")) {
          return mime.startsWith(token.slice(0, -1));
        }
        return mime === token;
      });
    },
    setError(message) {
      // Rejected files without a configured message still block, but show nothing.
      this.shownError = message || "";
      this.showError = message !== "" && message != null;
    },
    onFormValidate(event) {
      if (!this.field?.required || this.validFile) {
        this.setError("");
        return;
      }
      this.setError(this.field?.error_message || "");
      this.showError = true;
      event?.detail?.markInvalid?.();
      if (event?.detail && !event.detail.focusTarget) {
        event.detail.focusTarget = this.$refs.trigger || null;
      }
    },
  },
};
</script>
<style scoped>
.file-upload-field {
  position: relative;
  min-height: 42px;
}

.file-upload-dragover {
  border-color: #ef3d49 !important;
  background-color: #fff5f5;
}

.file-upload-trigger {
  min-height: 38px;
  gap: 8px;
  padding-left: 12px;
}

.file-upload-trigger:focus-visible {
  outline: 2px solid #ef3d49;
  outline-offset: 2px;
  border-radius: 21px;
}

.file-upload-label {
  flex-shrink: 0;
}

.file-upload-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-upload-placeholder {
  color: #757575;
}

.file-upload-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.file-upload-remove {
  border: none;
  background: transparent;
  padding: 0 12px 0 4px;
  font-size: 22px;
  line-height: 1;
  color: #383838;
  cursor: pointer;
}

.file-upload-input {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.required {
  color: red;
  margin-left: 5px;
}

.invalid-feedback.client-validate {
  display: none !important;
}

.invalid-feedback.client-validate.show {
  display: block !important;
}
</style>
